import { ContentType } from './ContentType';
import { FacetType } from './FacetTypes';
import { DataConnection } from '@tableau/search-microview-communication-channels';
import { ILocationInfo } from './ILocationInfo';
import { ExtractEncryptionState } from './DataSourceInfo';
import { IDataQualityIndicator, IPublishedDatasourceProperties } from './httpClient/MetaDataResults';
import { SearchResponseHitsContent } from '../../generated/content-exploration';
import { IUserInfo } from './IUserInfo';
import { IUser } from './IUser';
import { ISO8601FormattedString } from './ISO8601FormattedString';

export type CommonType = {
  name: string;
  id: string;
  lastAccessedAt?: Date;
  url?: string;
  contentType: ContentType;
  favorite?: boolean;
  crassData?: SearchResponseHitsContent;
  description?: string;
};
export type OwnerType = {
  //TODO: VPC Table data has a domain and a domainName which seems to be identical information. How to treat this?
  owner?: IUserInfo;
};
type ThumbnailType = { thumbnailUrl?: string };
type Location = { location?: ILocationInfo };
type DescriptionType = { description: string };
type WorkBookType = { workbook?: { id: string; name?: string; owner: IUserInfo } };
type CreatedTimeType = { createdAt?: string };
export type ModifiedTimeType = { modifiedTime?: string };

export type DataSourceConnectionDetails = {
  id?: string;
  name?: string;
  fileName?: string;
  siteUrl?: string;
  databaseUsername: string;
  hasEmbeddedPassword: boolean;
  instanceUrl?: string;
  serverName: string;
  type: string;
};

export type View = CommonType &
  OwnerType &
  ThumbnailType &
  ModifiedTimeType &
  Location &
  WorkBookType & {
    sheetNumber?: number;
    tags?: string[];
    hitsTotal?: number;
    project?: { id: string; name: string };
  };

export type DataQualityWarning = {
  assetName: string;
  assetType: string;
  assetUrl?: () => string;
  author: {};
  authorDisplayName: string;
  isActive: boolean;
  isSevere: boolean;
  message: string;
  updatedAt?: Date;
  vizportalId: string;
  warningType: string;
} & IDataQualityWarningAuthorInfo;

export type DataCatalogType = {
  __typename?: string;
  dataQualityWarnings?: IDataQualityWarningInfo[];
  dataQualityCertifications?: IDataQualityWarningInfo[];
  isCertified?: boolean;
  certifier?: ICatalogUserInfo;
  certificationNote?: string;
};

export type Metric = CommonType &
  OwnerType &
  Location &
  ModifiedTimeType & {
    suspended?: boolean;
    description?: string;
    tags?: string[];
    hitsTotal?: number;
    updatedAt?: ISO8601FormattedString;
  };

export type Workbook = CommonType &
  OwnerType &
  ThumbnailType &
  Location &
  ModifiedTimeType & {
    description?: string;
    tags?: string[];
    hitsTotal?: number;
    sheetCount?: number;
    size?: number;
  };

export type VirtualConnection = CommonType &
  OwnerType &
  Location &
  ModifiedTimeType & {
    dataQualityIndicators?: IDataQualityIndicator[];
    hasExtracts?: boolean;
  };

export type DataSource = CommonType &
  ModifiedTimeType &
  Location &
  OwnerType & {
    connectedWorkbooksCount?: number;
    connectionDetails?: DataConnection;
    publishedDatasourceDetails?: IPublishedDatasourceProperties;
    hasExtracts?: boolean;
    connectionType?: string;
    isFileBased?: boolean;
    dataConnectionsCount?: number;
    lastRefreshedAt?: string;
    extractEncryptionState?: ExtractEncryptionState;
    extractCreationPending?: boolean;
    dataFreshness?: Date;
    usageInfo?: { hitsLastThreeMonthsTotal: number };
    workbookId?: string;
    certificationDetails?: {
      isCertified: boolean;
      certifier?: IUser;
      certificationNote?: string;
    };
    contentDetail?: {
      dataQualityIndicators: IDataQualityIndicator[];
    };
    usageInfoMaxValues?: { hitsTotal: number; connectedWorkbooksCount: number };
    datasourceId: string;
  };
export type Project = CommonType & OwnerType & ModifiedTimeType & DescriptionType & { createdAt: string };
export type Flow = CommonType & OwnerType & Location & ModifiedTimeType & DescriptionType & CreatedTimeType & { favoritesTotal?: number };
export type PredictiveModel = CommonType & OwnerType & Location & ModifiedTimeType & DescriptionType & CreatedTimeType;
export type Database = CommonType &
  Location &
  DataCatalogType & {
    downstreamDataSourceCount?: number;
    downstreamWorkbookCount?: number;
    filePath?: string;
    requestUrl?: string;
    provider?: string;
    fileId?: string;
    hostName?: string;
    connectorUrl?: string;
    graphQLId?: string;
  };
export type Table = CommonType &
  Location &
  DataCatalogType & {
    createdAt?: Date;
    luid?: string;
    database?: {
      __typename: string;
      hostName?: string;
      isGrouped: boolean;
      name: string;
    };
    site?: {
      id?: string;
      luid: string;
      urlName: string;
    };
    updatedAt?: Date;
    workbookCount?: number;
    datasourceCount?: number;
    vizportalId: string;
    graphQLId?: string;
  };
export type Collection = CommonType &
  OwnerType &
  ModifiedTimeType &
  DescriptionType & { createdTime: string; itemCount?: number; visibility: string; itemNames?: string[]; luid?: string };
export type DataRole = CommonType & OwnerType & Location & ModifiedTimeType & DescriptionType & CreatedTimeType;
export type Lense = CommonType & OwnerType & Location & ModifiedTimeType & DescriptionType & CreatedTimeType;

export type SearchResultItem =
  | View
  | Metric
  | Workbook
  | DataSource
  | VirtualConnection
  | Project
  | Flow
  | Database
  | Table
  | Collection
  | DataRole
  | Lense
  | PredictiveModel;

export type SearchResultList<T> = {
  type: ContentType;
  list: T[];
  totalCount: number;
  facets?: FacetType;
  error?: string;
};

export type MixedContentTypeSearchResult = {
  contentTypeOrder?: ContentType[];
  map: { [type in ContentType]: SearchResultList<SearchResultItem> };
  error?: string;
};

export function getKeyForItem(item: SearchResultItem): string {
  return `${item.id}`;
}

export interface IDataQualityWarningAuthorInfo {
  __typename?: 'TableauUser';
  username: string;
  name: string;
  domain: string;
}

export interface IDataQualityWarningInfo {
  __typename?: 'DataQualityWarning';
  author: IDataQualityWarningAuthorInfo;
  authorDisplayName: string;
  isActive: boolean;
  isSevere: boolean;
  owner: IDataQualityWarningAuthorInfo;
  message: string;
  updatedAt?: Date;
  vizportalId: string;
  warningType: string;
  assetUrl: unknown;
  assetName: string;
  assetType: string;
}

export interface ICatalogUserInfo {
  id: string;
  __typename?: 'TableauUser';
  username: string;
  name: string;
  domain?: string;
  readOnly?: boolean;
  hasServerDisplay?: boolean;
}
